// useLivePrices · always polls /api/market/quotes for live ticks.
//
// We no longer connect to the SignalR hub directly from the browser because:
//   1. The hub does not advertise CORS for arbitrary preview origins.
//   2. Even when CORS passes, the browser would also need to invoke
//      `SyncSubscriptions(symbols)` to trigger streaming — and we'd need to
//      duplicate the entire symbol-discovery logic on the client.
// Instead, the FastAPI backend connects to the hub server-to-server (where
// it works flawlessly), caches every tick, and exposes them via the existing
// /api/market/quotes endpoint. We poll that every second and surface the data
// exactly as if it were arriving live.
//
// Status semantics · "connected" whenever ANY quote in the payload has a
// fresh timestamp (< STALE_S seconds old), regardless of whether the tick
// originated from the upstream SignalR hub or the local simulator. This
// prevents the UI from parking on "Reconnecting…" while the just4b hub is
// intermittently down — the simulator serves realistic prices in that window
// and there is nothing users can do about it, so we shouldn't alarm them.
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const POLL_MS = 2000;
const STALE_S = 15;   // treat quotes older than this as stale (should never happen · POLL_MS is 2s)

export default function useLivePrices() {
  const [prices, setPrices] = useState({});
  const [status, setStatus] = useState("connecting");
  const lastBidsRef = useRef({});

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/market/quotes?category=all");
        if (cancelled) return;
        const list = data?.quotes || [];
        const next = {};
        const lasts = lastBidsRef.current;
        const nowMs = Date.now();
        let freshCount = 0;
        for (const q of list) {
          if (!q?.symbol) continue;
          const prevBid = lasts[q.symbol];
          const dir =
            q.direction || (prevBid !== undefined
              ? (q.bid > prevBid ? "up" : q.bid < prevBid ? "down" : "")
              : "");
          // A quote is "fresh" if its server timestamp is within STALE_S seconds
          const tsMs = q.ts ? Date.parse(q.ts) : 0;
          const fresh = tsMs > 0 && (nowMs - tsMs) / 1000 <= STALE_S;
          if (fresh) freshCount++;
          next[q.symbol] = {
            symbol:     q.symbol,
            name:       q.name,
            bid:        q.bid,
            ask:        q.ask,
            spread:     q.spread,
            change:     q.change_pct ?? 0,
            dir,
            category:   q.category,
            subcategory:q.subcategory,
            live:       Boolean(q.live) || fresh,
            ts:         q.ts,
          };
          lasts[q.symbol] = q.bid;
        }
        setPrices(next);
        // "connected" if we're seeing fresh quotes (from either the real hub
        // OR the simulator). "fallback" if the response arrived but every
        // quote is stale · that means the backend is stuck.
        setStatus(freshCount > 0 ? "connected" : "fallback");
      } catch (err) {
        if (!cancelled) setStatus("disconnected");
      }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return { prices, status };
}
